import { useEffect, useState } from "react";
import { Row, Col, Select, Typography, Button, Spin, Drawer } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../services/api";
import Section from "../../components/Section";
import ProductDetailContent from "./ProductDetailContent";
const { Title } = Typography;

export default function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [discounted, setDiscounted] = useState([]);
  const [subcategoryTitle, setSubcategoryTitle] = useState("");
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [filters, setFilters] = useState({
    brand_id: null,
    model_id: null,
  });

  const openDetail = (product) => {
    setSelectedProduct(product);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    loadFilters();
    loadData(); // only once
  }, []);

  /* ---------------- LOAD FILTERS ---------------- */

  const loadFilters = async () => {
    const res = await api.get("/products/filters");
    setBrands(res.data.brands || []);
    setModels(res.data.models || []);
    setFilteredModels(res.data.models || []);
  };

  /* ---------------- LOAD DATA ---------------- */

  const loadData = async () => {
    try {
      setLoading(true);

      const params = {
        brand_id: filters.brand_id,
        model_id: filters.model_id,
      };

      const res = await api.get("/products/home", { params });

      setTrending(res.data.trending || []);
      setNewItems(res.data.new || []);
      setDiscounted(res.data.discounted || []);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  const allProducts = [...trending, ...newItems, ...discounted];
  /* ---------------- UI ---------------- */

  return (
    <>
      {/* 🔥 FILTERS */}
      <h3>Search product by its models:</h3>
      <Row gutter={16}>
        <Col>
          <Select
            placeholder="Select Brand"
            style={{ width: 250 }}
            allowClear
            value={filters.brand_id}
            onChange={(val) => {
              // update state only
              const updated = {
                brand_id: val,
                model_id: null,
              };

              setFilters(updated);

              // update model dropdown
              if (!val) {
                setFilteredModels(models);
              } else {
                setFilteredModels(models.filter((m) => m.brand_id === val));
              }
            }}
            options={brands.map((b) => ({
              value: b.id,
              label: b.name,
            }))}
          />
        </Col>
        <Col>
          <Select
            showSearch
            allowClear
            placeholder="Select Model"
            style={{ width: 250 }}
            value={filters.model_id}
            disabled={!filters.brand_id}
            optionFilterProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB, { searchValue }) => {
              const a = optionA.label.toLowerCase();
              const b = optionB.label.toLowerCase();
              const search = searchValue.toLowerCase();

              const aStarts = a.startsWith(search);
              const bStarts = b.startsWith(search);

              if (aStarts && !bStarts) return -1;
              if (!aStarts && bStarts) return 1;

              return a.localeCompare(b);
            }}
            onChange={(val) => {
              const updated = {
                ...filters,
                model_id: val,
              };

              setFilters(updated);

              if (val) {
                navigate(
                  `/product-list?brand_id=${updated.brand_id}&model_id=${val}`,
                );
              } else {
                navigate(`/product-list?brand_id=${updated.brand_id}`);
              }
            }}
            options={filteredModels.map((m) => ({
              value: m.id,
              label: m.name,
            }))}
          />
        </Col>
      </Row>

      {/* 🔽 SECTIONS */}

      {loading ? (
        <Spin style={{ marginTop: 50 }} />
      ) : (
        <>
          {/* 🔥 Sections */}
          <Section
            title="🔥 Trending"
            data={trending}
            loading={loading}
            onViewAll={() => navigate("/product-list?type=trending")}
            onProductClick={openDetail} // ✅ pass handler
          />

          <Section
            title="🆕 Newly Added"
            data={newItems}
            loading={loading}
            onViewAll={() => navigate("/product-list?type=new")}
            onProductClick={openDetail}
          />

          <Section
            title="💸 Discounted"
            data={discounted}
            loading={loading}
            onViewAll={() => navigate("/product-list?type=discounted")}
            onProductClick={openDetail}
          />
        </>
      )}

      {/* 🔥 Product Detail Drawer */}
      <Drawer
        width={750}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
      >
        {selectedProduct && (
          <ProductDetailContent
            product={selectedProduct}
            allProducts={allProducts}
          />
        )}
      </Drawer>
    </>
  );
}
